import * as React from "react";
// Controls
import { Button, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import { Dismiss } from "@ms/planner-generic-controls/lib/local/icons";
import { SecureLink } from "@ms/planner-generic-controls/lib/local/secureLink/SecureLink";
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { Temporal } from "temporal-polyfill-lite";
// Constants
import { LearnMoreLinks } from "@ms/uno-constants/lib/local/AppConstants";
// Resources
import { format } from "@fluentui/utilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Utilities
import { formatDate } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
/**
 * MeetingPlanExpirationMessageBar
 * This component displays a message bar in plan page which notifies user that an inactive meeting plan will be deleted.
 * The message is shown when a Roster plan is about to expire in the current month.
 */ export const MeetingPlanExpirationMessageBar = ({ expiryDate, onKeepPlanClicked, keepPlanOperationStatus, onDismissKeepPlanFailure, shouldShowRetainedMessage })=>{
    const [isRetainedDismissed, setIsRetainedDismissed] = React.useState(false);
    const [isFailureDismissed, setIsFailureDismissed] = React.useState(false);
    const isKeepPlanOperationRunning = keepPlanOperationStatus === OperationStatus.Running;
    const didKeepPlanOperationSucceed = keepPlanOperationStatus === OperationStatus.CompletedSuccessfully;
    if (didKeepPlanOperationSucceed && !isRetainedDismissed) {
        return /*#__PURE__*/ React.createElement(MessageBar, {
            role: "status",
            intent: "success",
            "aria-label": Strings.MyPlansPage_MeetingPlanSuccessfulRetentionTitle,
            "aria-labelledby": undefined
        }, /*#__PURE__*/ React.createElement(MessageBarBody, null, Strings.MyPlansPage_MeetingPlanSuccessfulRetentionTitle), /*#__PURE__*/ React.createElement(MessageBarActions, null, /*#__PURE__*/ React.createElement(Button, {
            appearance: "transparent",
            icon: /*#__PURE__*/ React.createElement(Dismiss, null),
            onClick: ()=>{
                setIsRetainedDismissed(true);
            },
            "aria-label": Strings.Notifications_NotificationBarDismissAriaLabel,
            "data-testid": "meeting-plan-expiration-retained-dismiss-button"
        })));
    }
    if (shouldShowRetainedMessage && !isRetainedDismissed) {
        return /*#__PURE__*/ React.createElement(MessageBar, {
            role: "status",
            intent: "info",
            "aria-label": Strings.MyPlansPage_MeetingPlanRetentionInfoTitle,
            "aria-labelledby": undefined
        }, /*#__PURE__*/ React.createElement(MessageBarBody, null, Strings.MyPlansPage_MeetingPlanRetentionInfoTitle), /*#__PURE__*/ React.createElement(MessageBarActions, null, /*#__PURE__*/ React.createElement(Button, {
            appearance: "transparent",
            icon: /*#__PURE__*/ React.createElement(Dismiss, null),
            onClick: ()=>{
                setIsRetainedDismissed(true);
            },
            "aria-label": Strings.Notifications_NotificationBarDismissAriaLabel,
            "data-testid": "meeting-plan-expiration-retained-dismiss-button"
        })));
    }
    if (didKeepPlanOperationSucceed) {
        return null;
    }
    // The plan is deleted at the end of the expiry month, so show the message bar within 30 days of that date.
    // Derive the month from the canonical instant date in UTC so a timezone boundary cannot shift the deletion month.
    const currentDate = Temporal.Now.zonedDateTimeISO();
    const currentDateInUtc = currentDate.withTimeZone("UTC");
    const currentTimeZone = currentDate.timeZoneId;
    const expiryPlainDate = expiryDate.toZonedDateTimeISO("UTC").toPlainDate();
    const endOfExpiryMonth = expiryPlainDate.with({
        day: expiryPlainDate.daysInMonth
    });
    const daysUntilEndOfExpiryMonth = currentDateInUtc.toPlainDate().until(endOfExpiryMonth, {
        largestUnit: "day"
    }).days;
    const isExpiringSoon = daysUntilEndOfExpiryMonth >= 0 && daysUntilEndOfExpiryMonth <= 30;
    if (!isExpiringSoon) {
        return null;
    }
    // Format the end-of-month date for display (actual deletion date)
    const expiryDateText = formatDate(endOfExpiryMonth.toZonedDateTime(currentTimeZone), true, "LL");
    switch(keepPlanOperationStatus){
        case OperationStatus.CompletedWithErrors:
            if (!isFailureDismissed) {
                return /*#__PURE__*/ React.createElement(MessageBar, {
                    role: "alert",
                    intent: "error"
                }, /*#__PURE__*/ React.createElement(MessageBarBody, null, /*#__PURE__*/ React.createElement(MessageBarTitle, null, Strings.MyPlansPage_MeetingPlanExpirationRetainPlanFailureTitle), format(Strings.MyPlansPage_MeetingPlanExpirationRetainPlanFailureMessage, expiryDateText)), /*#__PURE__*/ React.createElement(MessageBarActions, null, /*#__PURE__*/ React.createElement(Button, {
                    appearance: "secondary",
                    onClick: onKeepPlanClicked,
                    "data-testid": "meeting-plan-expiration-retry-keep-plan-button"
                }, Strings.MyPlansPage_MeetingPlanExpirationRetainPlanRetryButton), /*#__PURE__*/ React.createElement(Button, {
                    appearance: "transparent",
                    icon: /*#__PURE__*/ React.createElement(Dismiss, null),
                    onClick: ()=>{
                        setIsFailureDismissed(true);
                        onDismissKeepPlanFailure();
                    },
                    "aria-label": Strings.Notifications_NotificationBarDismissAriaLabel,
                    "data-testid": "meeting-plan-expiration-failure-dismiss-button"
                })));
            }
        // Fall through to warning bar after failure is dismissed
        case OperationStatus.Running:
        default:
            return /*#__PURE__*/ React.createElement(MessageBar, {
                role: "status",
                intent: "warning",
                "aria-busy": isKeepPlanOperationRunning
            }, /*#__PURE__*/ React.createElement(MessageBarBody, null, /*#__PURE__*/ React.createElement(MessageBarTitle, null, format(Strings.MyPlansPage_MeetingPlanExpirationMessageTitle, expiryDateText)), Strings.MyPlansPage_MeetingPlanExpirationMessageContent, " ", /*#__PURE__*/ React.createElement(SecureLink, {
                href: LearnMoreLinks.meetingPlansExpirationDeletionAndRecovery
            }, Strings.MyPlansPage_MeetingPlanExpirationLearnMoreLink)), /*#__PURE__*/ React.createElement(MessageBarActions, null, /*#__PURE__*/ React.createElement(Button, {
                appearance: "secondary",
                onClick: onKeepPlanClicked,
                disabled: isKeepPlanOperationRunning,
                "data-testid": "meeting-plan-expiration-keep-plan-button"
            }, Strings.MyPlansPage_MeetingPlanExpirationKeepPlanButton)));
    }
};
